import {
  createTheme as createMuiTheme,
  responsiveFontSizes,
} from '@mui/material/styles'
import DanaWoff from '../fonts/dana/woff/dana-regular.woff'
import markPro from '../fonts/ff-mark-pro.woff'
import { faIR, enUS } from '@mui/material/locale'
import darkScrollbar from '@mui/material/darkScrollbar'
import { customFonts } from 'settings'

const muiLocaleMap = {
  fa: faIR,
  en: enUS,
}

const getMuiLocale = (locale) => {
  return muiLocaleMap[locale] || enUS
}

const fonts = {
  farsi: customFonts.farsi ?? DanaWoff,
  latin: customFonts.latin ?? markPro,
}

const persianUnicodeRange =
  'U+20-21, U+24-25, U+27-3A, U+3C-3E, U+5B-5F, U+7C, U+A0, U+A9, U+AB, U+BB, U+609, U+60C, U+61B, U+61F, U+621-624, U+626-63A, U+641-642, U+644-648, U+64B-64D, U+651, U+654, U+66A-66C, U+67E, U+686, U+698, U+6A9, U+6AF, U+6CC, U+6F0-6F9, U+200E, U+2010-2011, U+2026, U+2030, U+2039-203A, U+20AC, U+2212'

const persianFont = `
font-family: 'dana';
font-style: normal;
font-display: swap;
font-weight: 400;
src: url(${fonts.farsi}) format('woff');
unicode-range: ${persianUnicodeRange}
`
const englishFont = `
font-family: 'ff-mark-pro';
font-style: normal;
font-display: swap;
font-weight: 100;
src: url(${fonts.latin}) format('woff');
`

// A custom theme for this app
const createTheme = ({ direction, locale }) => {
  const isLocaleRTL = direction === 'rtl'

  const theme = createMuiTheme(
    {
      mode: 'light',
      direction,
      palette: {
        text: {
          hint: 'rgba(0, 0, 0, 0.38)',
        },
        mode: 'light',
        type: 'light',
        primary: {
          main: '#8F6EFD',
          light: '#B9A4FF',
          dark: '#774FFF',
        },
        secondary: {
          main: '#0D1216',
        },
        error: {
          main: '#FF1E1E',
        },
        warning: {
          main: '#FF9D48',
        },
        textPrimary: {
          main: 'rgba(28, 35, 46, 1)',
        },
        textSecondary: {
          main: 'rgba(79, 89, 104, 1)',
        },
        tertiary: {
          main: '#24CBC3',
          dark: '#00CFDE',
          light: '#A9EFF2',
        },
        formaloo: { main: '#F95E2F' },
        lightFormaloo: { main: 'rgba(255, 55, 6, 0.25)' },
        gray: { main: '#CECECE' },
        lightGray: { main: '#F1F3F4' },
        customSuccess: { main: '#00B424' },
        lightBlue: { main: '#3498db' },
        customDark: { main: '#263238' },
        customBackground: { main: '#fafafa' },
        customInputBackground: { main: 'rgba(0,0,0,.05)' },
        gradient: {
          main: 'linear-gradient(270deg, rgba(75,73,255,1) 19%, rgba(153,82,162,1) 55%, rgba(249,92,48,1) 100%)',
        },
        white: '#fff',
      },
      typography: {
        fontFamily: ['ff-mark-pro', 'dana'].join(','),
        // fontSize: 16,
        // Tell Material-UI what's the font-size on the html element is.
        // htmlFontSize: 16,
      },
      // overrides
      components: {
        MuiCssBaseline: {
          styleOverrides: (themeParam) => `
          @font-face {
            ${isLocaleRTL ? persianFont : englishFont}
          }
          body {
            ${themeParam.palette.mode === 'dark' ? darkScrollbar() : null};
            font-size: 0.875rem;
            line-height: 1.43;
            // letter-spacing: 0.01071em;
          }
        `,
        },
        MuiButton: {
          defaultProps: {
            size: 'small',
            disableElevation: true,
            disableFocusRipple: true,
            disableTouchRipple: true,
          },
          styleOverrides: {
            root: {
              textTransform: 'capitalize',
              minHeight: 35,
              fontSize: 13,
              minWidth: 80,
              borderRadius: 6,
              lineHeight: 1,
              padding: '5px 12px',
            },
            outlinedSecondary: {
              background: 'white',
            },
            outlinedPrimary: {
              background: 'white',
              '&:hover': {
                backgroundColor: 'white',
              },
            },
          },
        },
        MuiFilledInput: {
          defaultProps: {
            size: 'small',
          },
        },
        MuiFormControl: {
          defaultProps: {
            margin: 'dense',
          },
        },
        MuiAppBar: {
          defaultProps: {
            enableColorOnDark: true,
            color: 'default',
          },
        },
        MuiFormHelperText: {
          defaultProps: {
            component: 'div',
            margin: 'dense',
          },
          styleOverrides: {
            contained: {
              marginLeft: 0,
              marginRight: 0,
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: { backgroundImage: 'unset' },
            rounded: {
              borderRadius: 6,
            },
            elevation4: {
              boxShadow: '0px 4px 6px rgba(0,0,0,0.16)',
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              fontSize: 13,
            },
          },
        },
        MuiInputBase: {
          defaultProps: {
            size: 'small',
          },
          styleOverrides: {
            input: {
              height: 'auto',
            },
          },
        },
        MuiOutlinedInput: {
          defaultProps: {
            size: 'small',
          },
        },
        MuiInputLabel: {
          defaultProps: {
            margin: 'dense',
          },
        },
        MuiListItem: {
          defaultProps: {
            dense: true,
          },
        },
        MuiFab: {
          defaultProps: {
            size: 'small',
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: {
              '&:hover': {
                backgroundColor: 'none',
              },
            },
            sizeSmall: {
              // Adjust spacing to reach minimal touch target hitbox
              marginLeft: 4,
              marginRight: 4,
            },
          },
        },
        MuiTable: {
          defaultProps: {
            size: 'small',
          },
          styleOverrides: {
            root: {
              fontSize: '0.875rem',
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              fontSize: '0.875rem',
            },
          },
        },
        MuiTextField: {
          defaultProps: {
            size: 'small',
          },
        },
        MuiLink: {
          defaultProps: {
            underline: 'hover',
          },
        },
        MuiToolbar: {
          defaultProps: {
            variant: 'dense',
          },
        },
        MuiAlert: {
          root: {
            borderRadius: 6,
          },
        },
      },
    },
    getMuiLocale(locale)
  )

  return responsiveFontSizes(theme)
}

export default createTheme
